import { last } from 'ramda';
import { EDCounter } from '../../../../../../BaseComponents/Counter/EDCounter';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { getAssociationUpdateValueObject } from '../../../../../../Model/avenyModelUtils';
import { AvenyCollectionMap } from '../../../../../../Model/ModelContext/ModelContext';
import { IBusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  getFieldValidationErrorMessage,
  getFieldValidationHintMessage,
  getFieldValidationWarningMessage,
  IFormData,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { getAttributeValues } from '../../../../_Common/Hooks/useAttributeValues';
import { StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All } from '../Queries/_QueryTypes/StatistikrapporteringAldersindelningQuery';
import { StatistikrapporteringQuery_node_TEDBOAktivitet__Associations_AktAlderindelning } from '../Queries/_QueryTypes/StatistikrapporteringQuery';
import { selectValue } from './Utils';

export interface ICounterProps {
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  hideButtons?: boolean;
  numericOnly?: boolean;
  testId?: string;
}

export const attributeCounter = (
  avenyCollectionMap: AvenyCollectionMap,
  formContext: IBusinessDataFormContext,
  attributeName: string,
  onChange: (
    formContext: IBusinessDataFormContext,
    attributeName: string,
    newValue: number | null
  ) => void,
  props?: ICounterProps
) => {
  const { value, attribute, valueEdited } = getAttributeValues(
    avenyCollectionMap,
    formContext,
    attributeName
  );

  const errorMessage = getFieldValidationErrorMessage(
    formContext?.formState.validation ?? [],
    attributeName
  );
  const hintMessage = getFieldValidationWarningMessage(
    formContext?.formState.validation ?? [],
    attributeName
  );

  return (
    <EDCounter
      value={value != null ? parseInt(value) : null}
      onChange={newValue => onChange(formContext, attributeName, newValue)}
      disabled={props?.disabled}
      label={props?.label || attribute?.DisplayLabel || ''}
      tooltip={props?.tooltip}
      hideButtons={props?.hideButtons}
      error={errorMessage !== undefined}
      warning={hintMessage !== undefined}
      helperText={errorMessage || hintMessage}
      marker={valueEdited}
      numericOnly={props?.numericOnly}
      testId={props?.testId}
    />
  );
};

export const onAttrCounterChange = (
  formContext: IBusinessDataFormContext,
  attributeName: string,
  newValue: number | null
) => {
  formContext?.formStateDispatch({
    type: 'UPDATE',
    value: getAssociationUpdateValueObject(
      attributeName,
      newValue,
      formContext.data ?? ({} as any)
    ),
  });
};

export const onDigitalCounterChange = (
  formContext: IBusinessDataFormContext,
  attributeName: string,
  newValue: number | null
) => {
  let oldvalues = formContext?.data?._Associations?.DigitalaVisningar;
  let editvalues =
    formContext?.formState?.values?._Associations
      ?.DigitalaVisningar_Add_Values ??
    formContext?.formState?.values?._Associations?.DigitalaVisningar_Values;
  const digValue = (sumAttributeName: string) =>
    attributeName === 'DigitalaVisningar.' + sumAttributeName
      ? newValue
      : selectValue(
          editvalues?.[sumAttributeName],
          oldvalues?.[sumAttributeName]
        );
  let values = [
    digValue('VISNINGAR_FACEBOOK'),
    digValue('VISNINGAR_YOUTUBE'),
    digValue('VISNINGAR_VIMEO'),
    digValue('VISNINGAR_OVRIG'),
  ];
  let sum = null;
  for (let i = 0; i < values.length; i++) {
    if (values[i] != null) {
      sum = (sum || 0) + (values[i] || 0);
    }
  }
  formContext?.update([
    {
      operation: 'Values',
      associationName: 'DigitalaVisningar',
      values: {
        VISNINGAR_TOTAL: sum,
        [last<string>(attributeName.split('.')) ?? '']: newValue,
      },
    },
  ]);
};

export const alderInitAntal = (
  data: INodeData | IFormData | undefined,
  id: string
) => {
  let aktAlderData = data?._Associations?.AktAlderindelning;
  if (aktAlderData) {
    for (let i = 0; i < aktAlderData.length; i++) {
      let alder = aktAlderData[i];
      if (
        alder._Associations?.Aldersindelning.id === id &&
        alder.ANTAL !== null
      ) {
        return alder.ANTAL;
      }
    }
  }
  return null;
};

export const alderCounters = (
  aktGiltigaAldrar: StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All[],
  formContext: IBusinessDataFormContext,
  disabled: boolean,
  numericOnly?: boolean
) => {
  let counters = [];
  for (let i = 0; i < aktGiltigaAldrar.length; i++) {
    let alder = aktGiltigaAldrar[i];
    const initValue = alderInitAntal(formContext?.data, alder.id);
    const editValue =
      formContext?.formState?.values?.aldrar &&
      formContext.formState.values.aldrar[alder.id];
    const errorMessage = formContext?.formState?.validation
      ? getFieldValidationErrorMessage(
          formContext.formState.validation,
          alder.id
        )
      : '';
    const warningMessage = formContext?.formState?.validation
      ? getFieldValidationWarningMessage(
          formContext.formState.validation,
          alder.id
        )
      : '';
    const hintMessage = formContext?.formState?.validation
      ? getFieldValidationHintMessage(
          formContext.formState.validation,
          alder.id
        )
      : '';
    counters.push(
      <EDCounter
        value={editValue === undefined ? initValue : editValue}
        marker={editValue !== undefined && editValue !== initValue}
        onChange={newValue =>
          onAlderCounterChange(
            aktGiltigaAldrar,
            formContext,
            alder.id,
            newValue
          )
        }
        error={errorMessage !== undefined}
        warning={warningMessage !== undefined}
        hint={hintMessage !== undefined}
        disabled={disabled}
        helperText={errorMessage || hintMessage}
        label={alder?.BENAMNING || ''}
        numericOnly={numericOnly}
      />
    );
  }
  return counters;
};

const onAlderCounterChange = (
  allaGiltigaAldrar: StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All[],
  formContext: IBusinessDataFormContext,
  id: string,
  newValue: number | null
) => {
  let oldvalues: StatistikrapporteringQuery_node_TEDBOAktivitet__Associations_AktAlderindelning[] =
    formContext?.data?._Associations?.AktAlderindelning || [];
  let editvalues = formContext?.formState?.values.aldrar || {};
  let sum = null;
  for (let i = 0; i < allaGiltigaAldrar.length; i++) {
    let alder = allaGiltigaAldrar[i];
    let value = null;
    if (alder.id === id) {
      value = newValue;
    } else if (editvalues[alder.id] !== undefined) {
      value = editvalues[alder.id];
    } else {
      value = oldvalues.find(
        a => a._Associations?.Aldersindelning.id === alder.id
      )?.ANTAL;
    }
    if (value != null) {
      sum = (sum || 0) + value;
    }
  }

  formContext?.update([
    {
      operation: 'OwnFields',
      values: {
        aldrar: {
          [id]: newValue,
        },
        ANTAL_BESOKARE: sum,
      },
    },
  ]);
};
